import { Box, IconButton, InputAdornment, TextField, TextFieldProps } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { useState } from 'react';
import { useTheme } from "@mui/material/styles";

type SearchFieldProps = TextFieldProps & {
	searchIconColor?: string;
	clearIconColor?: string;
	backgroundColor?: string;
	textColor?: string;
	placeholderColor?: string;
	borderColor?: string;
};

const SearchField: React.FC<SearchFieldProps> = ({
	InputProps: parentInputProps,
	searchIconColor = '#009BD8',
	clearIconColor = '#009BD8',
	backgroundColor,
	textColor,
	placeholderColor,
	borderColor,
	...props
}) => {
	const theme = useTheme();
	const colors = theme.palette;
	const defaultStart = (
		<InputAdornment position="start" sx={{ ml: 0.5 }}>
			<SearchIcon sx={{ paddingLeft: '5px', color: `${searchIconColor} !important` }} />
		</InputAdornment>
	);
	const mergedInputProps = {
		...parentInputProps,
		startAdornment: parentInputProps?.startAdornment ?? defaultStart,
	};
	return (
		<Box
			sx={{
				position: 'sticky',
				top: -16,
				zIndex: 1
			}}
		>
			<TextField
				variant="outlined"
				placeholder="Search"
				InputProps={mergedInputProps}
				sx={{
					backgroundColor: `${backgroundColor || colors.search?.background || 'transparent'} !important`,
					backdropFilter: 'blur(10px)',
					borderRadius: '4px',
					color: `${textColor || colors.white.main} !important`,
					width: '100%',
					input: {
						py: 1,
						px: 0,
						color: `${textColor || colors.white.main} !important`,
						'&::placeholder': {
							...theme.typography.filterText,
							color: `${placeholderColor || textColor || colors.white.main} !important`,
							opacity: 1
						}
					},
					'& .MuiOutlinedInput-root': {
						'& fieldset': {
							border: `1px solid ${borderColor} !important`
						},
						'&:hover fieldset': {
							border: `1px solid ${borderColor} !important`
						},
						'&.Mui-focused fieldset': {
							border: `1px solid ${borderColor} !important`
						},
						'&.Mui-focused': {
							border: `1px solid ${borderColor} !important`
						}
					}
				}}
				{...props}
			/>
		</Box>
	);
};

const SearchFilter = ({
	handleSearchData,
	hasClearBtn = false,
	searchIconColor = '#009BD8',
	clearIconColor = '#009BD8',
	backgroundColor,
	textColor,
	placeholderColor,
	borderColor
}: any) => {
    const [searchInputText, setSearchInputText] = useState('');

	const handleClear = (e: any) => {
		e.stopPropagation();
		setSearchInputText('');
		handleSearchData?.('');
	};

    const handleSearch = (e: any) => {
		e.stopPropagation();
		const searchKeyword = (e.target as HTMLInputElement).value.toLowerCase();
		setSearchInputText(searchKeyword);
		if (searchKeyword?.length >= 3 || !searchKeyword) {
			handleSearchData?.(searchKeyword);
		}
	};

    return (
		<SearchField
            id="searchField"
            autoComplete="off"
            variant="outlined"
            placeholder="Search"
            value={searchInputText}
            onChange={(e: any) => {
                handleSearch(e);
            }}
			searchIconColor={searchIconColor}
			clearIconColor={clearIconColor}
			backgroundColor={backgroundColor}
			textColor={textColor}
			placeholderColor={placeholderColor}
			borderColor={borderColor}
			InputProps={{
				endAdornment: searchInputText && hasClearBtn ? (
					<InputAdornment position="end">
						<IconButton onClick={handleClear} size="small" sx={{ color: `${clearIconColor} !important` }}>
							<CloseIcon sx={{ fontSize: 20, color: `${clearIconColor} !important` }} />
						</IconButton>
					</InputAdornment>
				) : null
			}}
        />
	);
};

export default SearchFilter;
